/**
 * Chat API client — streams chat completions from the POD backend via Server-Sent Events.
 *
 * Usage:
 *   const { cancel } = streamChat(messages, {
 *       onToken: (text) => appendToUI(text),
 *       onToolCall: (toolName) => showToolIndicator(toolName),
 *       onToolResult: (toolName) => hideToolIndicator(toolName),
 *       onError: (err) => showError(err),
 *       onDone: () => markComplete(),
 *   });
 */

/**
 * Stream a chat conversation to the backend.
 *
 * @param {Array<{role: string, content: string}>} messages - Conversation history
 * @param {Object} callbacks - Event callbacks
 * @param {function(string): void} callbacks.onToken - Called for each text token
 * @param {function(): void} callbacks.onThinking - Called when the LLM starts a new thinking round
 * @param {function(string): void} callbacks.onToolCall - Called when a tool is invoked
 * @param {function(string): void} callbacks.onToolResult - Called when a tool completes
 * @param {function(string): void} callbacks.onError - Called on error
 * @param {function(): void} callbacks.onDone - Called when the stream is complete
 * @returns {{cancel: function(): void}} Control object with a cancel method
 */
export function streamChat(messages, callbacks = {}) {
    const controller = new AbortController();

    const url = `${process.env.REACT_APP_API_URL}Chat`;

    fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
        signal: controller.signal,
    })
        .then(async (response) => {
            if (!response.ok) {
                const text = await response.text().catch(() => response.statusText);
                callbacks.onError?.(text || `HTTP ${response.status}`);
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                // SSE events are separated by double newlines
                let idx;
                while ((idx = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);

                    // Parse SSE "data: ..." lines
                    const lines = rawEvent.split('\n');
                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed.startsWith('data:')) continue;
                        const jsonStr = trimmed.slice(5).trim();
                        if (!jsonStr) continue;

                        try {
                            const chunk = JSON.parse(jsonStr);
                            handleChunk(chunk, callbacks);
                        } catch {
                            // Ignore malformed JSON
                        }
                    }
                }
            }

            callbacks.onDone?.();
        })
        .catch((err) => {
            if (err.name === 'AbortError') return;
            callbacks.onError?.(err.message || 'Network error');
        });

    return {
        cancel: () => controller.abort(),
    };
}

/**
 * Handle a single SSE chunk from the backend.
 */
function handleChunk(chunk, callbacks) {
    switch (chunk.type) {
        case 'token':
            callbacks.onToken?.(chunk.content || '');
            break;
        case 'speech':
            callbacks.onSpeech?.(chunk.content || '');
            break;
        case 'thinking':
            callbacks.onThinking?.();
            break;
        case 'tool_call':
            callbacks.onToolCall?.(chunk.toolName || '');
            break;
        case 'tool_result':
            callbacks.onToolResult?.(chunk.toolName || '', chunk.toolResultData || null);
            break;
        case 'choice':
            callbacks.onChoice?.(chunk.choice || {});
            break;
        case 'error':
            callbacks.onError?.(chunk.error || 'Unknown error');
            break;
        case 'done':
            // Stream complete — onDone is called after the reader finishes
            break;
        default:
            break;
    }
}

/**
 * Pre-warm the backend prompt cache for the logged-in user. Fire-and-forget —
 * intended to be called when the chat page opens so the first real question
 * skips the expensive system-prompt + tool-schema prefill (lower TTFT).
 *
 * @returns {Promise<{warmed: boolean, promptTokens?: number, elapsedMs?: number}|null>}
 *     The warmup result, or null if the warmup failed (harmless — the next
 *     real request simply pays full prefill).
 */
export async function warmUpChat() {
    const url = `${process.env.REACT_APP_API_URL}Chat/warmup`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) return null;
        return await response.json();
    } catch {
        // Warmup is best-effort; network/auth failures are not user-actionable
        return null;
    }
}

/**
 * Resolve a pending choice by sending the user's selection to the backend.
 *
 * @param {string} choiceId - The choice ID from the choice chunk
 * @param {string} selectedId - The ID of the selected option
 * @returns {Promise<boolean>} True if the choice was resolved successfully
 */
export async function resolveChoice(choiceId, selectedId) {    const url = `${process.env.REACT_APP_API_URL}Chat/choice`;
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choiceId, selectedId }),
    });
    return response.ok;
}

/**
 * Record a like/dislike on an assistant answer.
 *
 * @param {Object} feedback
 * @param {'like'|'dislike'} feedback.rating - The user's rating
 * @param {string} feedback.question - The user message the answer responded to
 * @param {string} feedback.answer - The assistant answer being rated
 */
export async function submitChatFeedback({ rating, question, answer }) {
    const url = `${process.env.REACT_APP_API_URL}Chat/feedback`;
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, question, answer }),
    });
    if (!response.ok) {
        throw new Error(response.statusText || `Server returned ${response.status}`);
    }
}
